import { useState } from 'react';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useSidebar } from '@/hooks/useSidebar';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';

import { DeleteSpeechModelModal } from './DeleteSpeechModelModal';
import { SPEECH_PROVIDER_ICONS, SPEECH_PROVIDER_NAMES } from './helper';
import { ResourceDeleteButton } from '../../components/ResourceList/ResourceDeleteButton';
import { useSpeechModelForm } from '../../hooks/useSpeechModelForm';
import { ELEVENLABS_REGIONS, SpeechModel } from '../../types';

const PanelTitle = ({ title, onClose }: { title: string; onClose: () => void }) => {
  const { t } = useTranslation();

  return (
    <SidebarWrapperTitle className="pr-4 pl-6">
      <span className="text-base font-medium text-foreground">{title}</span>
      <Button
        variant="ghost-neutral"
        size="icon-sm"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <XIcon size={16} />
      </Button>
    </SidebarWrapperTitle>
  );
};

interface SpeechModelFormProps {
  speechModel?: SpeechModel;
  onClose: () => void;
}

/** Create or edit a speech model, with Delete under the last field when editing. */
const SpeechModelForm = ({ speechModel, onClose }: SpeechModelFormProps) => {
  const { t } = useTranslation();
  const { canWrite, canDelete } = useSpeechModels();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { form, onSubmit, isEdit, providerOptions, showRegion } = useSpeechModelForm(
    speechModel,
    onClose
  );
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;

  return (
    <SidebarWrapper>
      <PanelTitle
        title={
          isEdit
            ? t('developer_tools.speech_models.edit_title')
            : t('developer_tools.speech_models.create_title')
        }
        onClose={onClose}
      />

      <Form {...form}>
        <SidebarWrapperForm onSubmit={handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-1">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.language_models_provider')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                    <FormControl>
                      <SelectTrigger className="w-full" onBlur={field.onBlur}>
                        <SelectValue placeholder={t('common.select_dots')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {speechModel && (
                        <SelectItem value={speechModel.provider}>
                          {SPEECH_PROVIDER_NAMES[speechModel.provider]}
                        </SelectItem>
                      )}
                      {!isEdit &&
                        providerOptions.map((option) => (
                          <SelectItem
                            key={option.provider}
                            value={option.provider}
                            disabled={option.disabled}
                          >
                            <span className="flex items-center gap-2">
                              {SPEECH_PROVIDER_ICONS[option.provider]}
                              {SPEECH_PROVIDER_NAMES[option.provider] ?? option.provider}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="api_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('developer_tools.sidebar_api_key')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      autoComplete="new-password"
                      placeholder={speechModel?.config.api_key ?? t('developer_tools.type_api_key')}
                    />
                  </FormControl>
                  {isEdit && (
                    <FormDescription>
                      {t('developer_tools.speech_models.keep_api_key')}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {showRegion && (
              <FormField
                control={control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('developer_tools.speech_models.region')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full" onBlur={field.onBlur}>
                          <SelectValue placeholder={t('common.select_dots')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ELEVENLABS_REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {speechModel && (
              <ResourceDeleteButton onClick={() => setIsDeleteOpen(true)} disabled={!canDelete} />
            )}
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" disabled={!isDirty || !canWrite} isLoading={isSubmitting}>
              {isEdit ? t('common.save') : t('common.create')}
            </Button>
            <Button type="button" variant="ghost-neutral" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>

      {isDeleteOpen && speechModel && (
        <DeleteSpeechModelModal
          speechModel={speechModel}
          onClose={() => setIsDeleteOpen(false)}
          onDeleted={onClose}
        />
      )}
    </SidebarWrapper>
  );
};

/**
 * The speech model side panel. An edit waits for the list, so the form starts from the saved
 * values instead of empty ones; a model that is gone leaves only the header to close it.
 */
export const SpeechModelPanel = () => {
  const { t } = useTranslation();
  const { meta, close } = useSidebar();
  const { speechModels, listStatus } = useSpeechModels();
  const speechModelId = meta?.speechModelId;
  const speechModel = speechModels.find((model) => model.speech_model_id === speechModelId);

  if (speechModelId && !speechModel) {
    return (
      <SidebarWrapper>
        <PanelTitle title={t('developer_tools.speech_models.edit_title')} onClose={close} />
        {listStatus === 'pending' && (
          <div className="space-y-4 p-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
      </SidebarWrapper>
    );
  }

  return <SpeechModelForm key={speechModelId ?? 'new'} speechModel={speechModel} onClose={close} />;
};
